import { ProCard } from '@ant-design/pro-components'
import Logo from '@app/icare/assets/logo-green.svg?inline'
import { useAuth } from '@app/icare/client/auth'
import { firstEligible } from '@app/icare/module/tour/sequence'
import { useRouteTour } from '@app/icare/module/tour/useRouteTour'
import { t } from '@lingui/core/macro'
import { Trans } from '@lingui/react/macro'
import { mergeNonEmptyStrings } from '@pkg/shared/utils'
import { ButtonLink } from '@pkg/ui/button'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Button, Tour } from 'antd'
import { useRef } from 'react'

export const Route = createFileRoute('/_app/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { current, allowed, ability } = useAuth()
  const navigate = useNavigate()
  const launchButtonRef = useRef<HTMLButtonElement>(null)
  const username = mergeNonEmptyStrings([current?.firstName, current?.lastName]) || current?.username || ''

  const handleStartTour = () => {
    const first = firstEligible(ability)
    if (first) void navigate({ to: '.', search: { tour: first, tourStep: 0 }, replace: true })
  }

  const introTour = useRouteTour('home-intro', [
    {
      title: t`Willkommen`,
      description: t`Willkommen bei iCare! Hier ist eine kurze Tour, um dir den Einstieg zu erleichtern.`,
      target: null,
    },
  ])

  const outroTour = useRouteTour('home-outro', [
    {
      title: t`Tour erneut starten`,
      description: t`Hier kannst du die Tour jederzeit erneut starten.`,
      target: () => launchButtonRef.current,
      placement: 'bottom',
    },
    {
      title: t`Viel Erfolg!`,
      description: t`Das war die kurze Tour. Viel Erfolg bei der Arbeit mit iCare!`,
      target: null,
    },
  ])

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] gap-y-8" data-testid="welcome-heading">
      <ProCard title={t`Willkommen bei iCare`}>
        <div className="flex flex-col items-center gap-y-6">
          <img src={Logo} alt="iCare Logo" className="max-h-16 my-4" />

          <p className="text-xl text-center">
            <Trans>Hallo {username}, schön dass du da bist!</Trans>
          </p>

          <div className="flex flex-col gap-4 w-full">
            {allowed('read', '/notifications') && (
              <ButtonLink to="/notifications" type="primary" block className="w-full text-lg py-3">
                <Trans>Zu den Meldungen</Trans>
              </ButtonLink>
            )}
            {allowed('read', '/my-patients') && (
              <ButtonLink to="/my-patients" type="primary" block className="w-full text-lg py-3">
                <Trans>Zu meinen Patienten</Trans>
              </ButtonLink>
            )}

            <Button
              ref={launchButtonRef}
              type="default"
              block
              className="w-full text-lg py-3"
              onClick={handleStartTour}
            >
              <Trans>Onboarding-Tour starten</Trans>
            </Button>
          </div>
        </div>
      </ProCard>
      <Tour {...introTour} />
      <Tour {...outroTour} />
    </div>
  )
}
